// THE ORBITAL CHART (docs/space-game-mode-spec.md §9): the plotting table,
// reborn. The battleship's plot surface becomes the x/h side-view of the sky
// — the ribbon unrolled, exactly the figure at the top of the spec, live.
// Your predicted path is the parity kernel propagated a few minutes ahead
// (hundreds of multiplies, nothing), spotted contacts fly their own short
// arcs, the station rides its real longitude off the wire, the relays stand
// on the ground they own, the terminator is a place, and — for the Hammer —
// the rod plot marks where a release NOW would strike. Everything drawn here
// is either already on the wire or computed by the same arithmetic the
// server flies, so the chart can never promise an orbit the world would
// refuse.
//
// Toggled with M (the map key; Z stays the gunsight elsewhere). Additive and
// theater-gated per the constitution: no other theater constructs one.

import { stepOrbit, wrapDeltaX } from "./orbit_core.js";

export class SpaceChart {
  /** consts: {wrapM, R, mu, ceiling}; field: the terrain mirror (surfaceAt). */
  constructor(consts, field) {
    this.c = consts;
    this.field = field;
    this.visible = false;

    const cv = document.createElement("canvas");
    cv.id = "space-chart";
    cv.width = 760;
    cv.height = 300;
    cv.style.cssText =
      "position:absolute;left:50%;transform:translateX(-50%);bottom:120px;z-index:7;" +
      "width:min(760px,92vw);border:1px solid #3a4a63;border-radius:6px;" +
      "background:rgba(6,10,18,0.88);display:none;pointer-events:none;";
    document.body.appendChild(cv);
    this.cv = cv;
    this.ctx = cv.getContext("2d");
  }

  toggle() {
    this.visible = !this.visible;
    this.cv.style.display = this.visible ? "block" : "none";
    return this.visible;
  }

  setVisible(on) {
    this.visible = !!on;
    this.cv.style.display = on ? "block" : "none";
  }

  /** World x (absolute) → canvas px, centered on the own craft. */
  px(x, selfX) {
    return this.cv.width * (0.5 + wrapDeltaX(x, selfX, this.c.wrapM) / this.c.wrapM);
  }

  /** Altitude → canvas py (a margin above the ceiling so the roof reads). */
  py(h) {
    const top = this.c.ceiling + 400;
    return this.cv.height * (1 - Math.max(0, Math.min(h, top)) / top) * 0.94 + 8;
  }

  /**
   * Redraw. src carries everything the frame knows:
   *  pos/vel (own, predicted), landed, selfTeam,
   *  contacts: [{x, y, vx, vy, team}] (spotted craft on the wire),
   *  stationX (live longitude or null), zones: [{x, y, r, orbital}],
   *  holders: [i8...], rodImpactX (or null), apo, peri.
   */
  draw(src) {
    const { ctx, cv, c } = this;
    const W = cv.width, H = cv.height;
    const sx = src.pos.x;
    ctx.clearRect(0, 0, W, H);

    // --- the terminator: longitude IS time of day, so the night half is a
    // band you can see coming. The sun is fixed at 0.25 of the wrap.
    for (let px = 0; px < W; px += 8) {
      const wx = sx + (px / W - 0.5) * c.wrapM;
      let frac = wx / c.wrapM;
      frac -= Math.floor(frac);
      const elev = Math.cos((frac - 0.25) * 2 * Math.PI);
      if (elev <= 0) {
        ctx.fillStyle = `rgba(2,4,10,${0.25 + 0.3 * Math.min(1, -elev * 2)})`;
        ctx.fillRect(px, 0, 8, H);
      }
    }

    // --- the regolith: the analytic heightfield, sampled coarse. The chart
    // shows the ground under the ORBIT PLANE (own z), which is the ground
    // that can actually ruin your periapsis.
    ctx.beginPath();
    ctx.moveTo(0, H);
    const zRef = src.pos.z ?? 0;
    for (let px = 0; px <= W; px += 4) {
      const wx = sx + (px / W - 0.5) * c.wrapM;
      ctx.lineTo(px, this.py(this.field?.surfaceAt?.(wx, zRef) ?? 0));
    }
    ctx.lineTo(W, H);
    ctx.closePath();
    ctx.fillStyle = "#2a2d33";
    ctx.fill();

    // --- the roof: the war's edge, drawn like it means it.
    ctx.strokeStyle = "rgba(255,90,74,0.55)";
    ctx.setLineDash([6, 5]);
    ctx.beginPath();
    ctx.moveTo(0, this.py(c.ceiling));
    ctx.lineTo(W, this.py(c.ceiling));
    ctx.stroke();
    ctx.setLineDash([]);

    // --- prizes: the station (live longitude) with its bubble, the relays
    // with their footprints — painted in the holder's colour.
    const teamCol = (h) => (h === 0 ? "#e84a4a" : h === 1 ? "#4a7de8" : "#9aa3ad");
    (src.zones ?? []).forEach((z, i) => {
      const holder = src.holders?.[i] ?? -1;
      const zx = z.orbital && src.stationX != null ? src.stationX : z.x;
      const px = this.px(zx, sx);
      const py = this.py(z.orbital ? z.y : this.field?.surfaceAt?.(zx, z.z ?? 0) ?? 0);
      ctx.strokeStyle = teamCol(holder);
      ctx.fillStyle = teamCol(holder);
      if (z.orbital) {
        // The wheel and its rendezvous bubble, to scale in x.
        const rpx = (z.r / c.wrapM) * W;
        ctx.beginPath();
        ctx.arc(px, py, Math.max(rpx, 5), 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillRect(px - 2.5, py - 2.5, 5, 5);
      } else {
        // A mast on the ground, footprint ticks either side.
        ctx.fillRect(px - 1.5, py - 8, 3, 8);
        const rpx = (z.r / c.wrapM) * W;
        ctx.fillRect(px - rpx, py - 1.5, rpx * 2, 1.5);
      }
    });

    // --- contact arcs: everyone the wire admits to, flown 90 s ahead on the
    // same kernel. Enemies burn red, friends cool blue; the arc is the
    // theater's whole argument — where they ARE matters less than where
    // their orbit is taking them.
    for (const ct of src.contacts ?? []) {
      const enemy = ct.team !== src.selfTeam;
      ctx.strokeStyle = enemy ? "rgba(255,107,107,0.7)" : "rgba(122,167,255,0.6)";
      this.arc(ct.x, ct.y, ct.vx, ct.vy, 90, sx);
      ctx.fillStyle = enemy ? "#ff6b6b" : "#7aa7ff";
      ctx.fillRect(this.px(ct.x, sx) - 2, this.py(ct.y) - 2, 4, 4);
    }

    // --- own path: four minutes of the future, plus the apsis labels the
    // chips already taught. Drawn last so it rides over everything.
    ctx.strokeStyle = "#ffd76a";
    this.arc(src.pos.x, src.pos.y, src.vel.x, src.vel.y, 240, sx, src.vel.z, src.pos.z);
    ctx.fillStyle = "#fff";
    ctx.fillRect(W / 2 - 2.5, this.py(src.pos.y) - 2.5, 5, 5);

    // --- the rod plot: the barge's whole gunnery, one X on the ground.
    if (src.rodImpactX != null) {
      const px = this.px(src.rodImpactX, sx);
      const py = this.py(this.field?.surfaceAt?.(src.rodImpactX, zRef) ?? 0);
      ctx.strokeStyle = "#ff6b6b";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px - 5, py - 5);
      ctx.lineTo(px + 5, py + 5);
      ctx.moveTo(px + 5, py - 5);
      ctx.lineTo(px - 5, py + 5);
      ctx.stroke();
      ctx.lineWidth = 1;
    }

    // --- margins: the scale and the manners.
    ctx.fillStyle = "#8fa3c0";
    ctx.font = "10px 'Courier New',monospace";
    ctx.fillText("THE PLOT — the sky unrolled · ▲ you · — 4 min ahead", 8, 12);
    ctx.fillText(`ROOF ${Math.round(c.ceiling)} m`, W - 88, this.py(c.ceiling) - 4);
  }

  /** One kernel-flown arc from (x,h,vx,vh) for `sec` seconds. The pen lifts
   *  across the wrap seam (shortest-way px snaps edge to edge there), so a
   *  long orbit draws as two clean strokes instead of a slash across the map. */
  arc(x, h, vx, vh, sec, selfX, vz = 0, z = 0) {
    const { ctx, c, cv } = this;
    const s = { x, h, z, vx, vh, vz };
    ctx.beginPath();
    let prev = this.px(s.x, selfX);
    ctx.moveTo(prev, this.py(s.h));
    const dt = 1.0;
    for (let t = 0; t < sec; t += dt) {
      stepOrbit(s, c.mu, c.R, c.wrapM, dt, 0, 0, 0);
      if (s.h <= (this.field?.surfaceAt?.(s.x, s.z) ?? 0)) break; // the arc ends where arcs end
      const px = this.px(s.x, selfX);
      if (Math.abs(px - prev) > cv.width * 0.5) ctx.moveTo(px, this.py(s.h));
      else ctx.lineTo(px, this.py(s.h));
      prev = px;
    }
    ctx.stroke();
  }
}
